<template>
  <ModalComp
    :is-visible="isVisible"
    @update:is-visible="$emit('update:is-visible', $event)"
    title="Modifier le paquet"
    size="lg"
    :hide-footer="true"
  >
    <template #body>
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Titre du paquet -->
        <div>
          <label for="edit-titre" class="block text-sm font-medium text-black mb-2">
            Nom du paquet
          </label>
          <input 
            id="edit-titre"
            v-model="formData.titre"
            type="text"
            placeholder="Ex: Pack Standard, 1-1-1-1-1 (optionnel)"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label for="edit-nombre-tailles" class="block text-sm font-medium text-black mb-2">
            Nombre de tailles du paquet
          </label>
          <select id="edit-nombre-tailles" v-model.number="formData.nombreTailles" class="w-full px-3 py-2 border border-gray-300 rounded-md">
            <option v-for="nombreTailles in 12" :key="nombreTailles" :value="nombreTailles">{{ nombreTailles }}</option>
          </select>
        </div>

        <!-- Section des quantités -->
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <label class="block text-sm font-medium text-black">
              Quantités par taille
            </label>
            <div class="text-sm text-black">
              Colonnes p1 à p{{ formData.nombreTailles }}
            </div>
          </div>
          
          <div class="grid grid-cols-6 gap-3">
            <div 
              v-for="(quantite, index) in visibleQuantityColumns" 
              :key="index"
              class="text-center"
            >
              <label :for="`p${index + 1}`" class="block text-xs font-medium text-black mb-1">
                p{{ index + 1 }}
              </label>
              <div 
                class="px-2 py-1 rounded-md border"
                :class="quantite > 0 ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'"
              >
                <input 
                  :id="`p${index + 1}`"
                  v-model.number="quantityColumns[index]"
                  type="number"
                  min="1"
                  step="1"
                  class="w-full text-center bg-transparent border-none outline-none text-sm font-medium"
                  :class="quantite > 0 ? 'text-blue-700' : 'text-black'"
                  @blur="cleanQuantity(index)"
                />
              </div>
            </div>
          </div>
          
          <!-- Message d'erreur pour les quantités -->
          <p v-if="errors.quantites" class="text-sm text-red-600">
            {{ errors.quantites }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button 
            type="button"
            @click="$emit('update:is-visible', false)"
            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Annuler
          </button>
          <button 
            type="submit"
            :disabled="isSubmitting || !isFormValid"
            class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else class="fas fa-save mr-2"></i>
            {{ isSubmitting ? 'Sauvegarde...' : 'Sauvegarder les modifications' }}
          </button>
        </div>
      </form>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue'
import { ModalComp } from '@/components'
import type { Pack } from '@/types'

interface Props {
  isVisible: boolean
  pack: Pack | null
}

interface Emits {
  (e: 'update:is-visible', value: boolean): void
  (e: 'submit', pack: Pack): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const isSubmitting = ref(false)

const formData = reactive({
  id_pack: 0,
  id_vendeur: 0,
  titre: '',
  nombreTailles: 1,
})

const errors = reactive({
  titre: '',
  quantites: ''
})

// Colonnes de quantités réactives
const quantityColumns = ref<number[]>([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0])
const initialQuantityColumns = ref<number[]>([])
const initialNombreTailles = ref(1)
const visibleQuantityColumns = computed(() => quantityColumns.value.slice(0, formData.nombreTailles))
const legacyQuantitiesAreUnchanged = computed(() => {
  return formData.nombreTailles === initialNombreTailles.value
    && quantityColumns.value.every((quantity, index) => quantity === initialQuantityColumns.value[index])
})

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  const quantitiesAreStrictlyValid = quantityColumns.value
    .slice(0, formData.nombreTailles)
    .every(qty => Number.isInteger(qty) && qty > 0)
  return quantitiesAreStrictlyValid || legacyQuantitiesAreUnchanged.value
})

// Fonction pour nettoyer une quantité quand l'utilisateur quitte le champ
const cleanQuantity = (index: number) => {
  const value = quantityColumns.value[index]
  if (value === null || value === undefined || isNaN(Number(value))) {
    quantityColumns.value[index] = 0
  } else {
    quantityColumns.value[index] = Math.max(0, Number(value)) // S'assurer que c'est >= 0
  }
}

// Initialiser le formulaire quand le pack change
watch(() => props.pack, (newPack) => {
  if (newPack) {
    formData.id_pack = newPack.id_pack
    formData.id_vendeur = newPack.id_vendeur
    formData.titre = newPack.titre || ''
    const quantities = [
      newPack.p1, newPack.p2, newPack.p3, newPack.p4, newPack.p5, newPack.p6,
      newPack.p7, newPack.p8, newPack.p9, newPack.p10, newPack.p11, newPack.p12,
    ]
    formData.nombreTailles = quantities.reduce(
      (lastIndex, quantity, index) => quantity > 0 ? index + 1 : lastIndex,
      1
    )
    initialNombreTailles.value = formData.nombreTailles
    initialQuantityColumns.value = [...quantities]
    
    // Mettre à jour les colonnes de quantités
    quantityColumns.value = [
      newPack.p1,
      newPack.p2,
      newPack.p3,
      newPack.p4,
      newPack.p5,
      newPack.p6,
      newPack.p7,
      newPack.p8,
      newPack.p9,
      newPack.p10,
      newPack.p11,
      newPack.p12,
    ]
  }
}, { immediate: true })

const validateForm = () => {
  errors.titre = ''
  errors.quantites = ''
  
  const hasInvalidQuantity = quantityColumns.value
    .slice(0, formData.nombreTailles)
    .some(qty => !Number.isInteger(qty) || qty <= 0)
  if (hasInvalidQuantity && !legacyQuantitiesAreUnchanged.value) {
    errors.quantites = 'Toutes les quantités doivent être des entiers strictement positifs'
    return false
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isSubmitting.value = true
  
  try {
    // Nettoyer les valeurs : convertir les valeurs vides/null/undefined en 0
    const cleanQuantities = quantityColumns.value.map(qty => {
      if (qty === null || qty === undefined || isNaN(Number(qty))) {
        return 0
      }
      return Number(qty)
    })
    
    const packToUpdate: Pack = {
      id_pack: formData.id_pack,
      id_vendeur: formData.id_vendeur,
      titre: formData.titre || null,
      p1: formData.nombreTailles >= 1 ? cleanQuantities[0] : 0,
      p2: formData.nombreTailles >= 2 ? cleanQuantities[1] : 0,
      p3: formData.nombreTailles >= 3 ? cleanQuantities[2] : 0,
      p4: formData.nombreTailles >= 4 ? cleanQuantities[3] : 0,
      p5: formData.nombreTailles >= 5 ? cleanQuantities[4] : 0,
      p6: formData.nombreTailles >= 6 ? cleanQuantities[5] : 0,
      p7: formData.nombreTailles >= 7 ? cleanQuantities[6] : 0,
      p8: formData.nombreTailles >= 8 ? cleanQuantities[7] : 0,
      p9: formData.nombreTailles >= 9 ? cleanQuantities[8] : 0,
      p10: formData.nombreTailles >= 10 ? cleanQuantities[9] : 0,
      p11: formData.nombreTailles >= 11 ? cleanQuantities[10] : 0,
      p12: formData.nombreTailles >= 12 ? cleanQuantities[11] : 0,
    }
    
    emit('submit', packToUpdate)
  } catch (error) {
    console.error('Erreur lors de la modification:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>
